'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Undo2 } from 'lucide-react';
import type { Registration } from '@/features/registration';
import {
  useSetPageCountMutation,
  useRequestExtraStickingLabelsMutation,
  useAdjustActualPageCountMutation,
  useReturnToRegistrationMutation
} from '../services/sticking.mutations';

interface StickingActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registration: Registration;
}

export function StickingActionsDialog({
  open,
  onOpenChange,
  registration
}: StickingActionsDialogProps) {
  const t = useTranslations('sticking');

  const hasPageCount = registration.page_count != null;
  const initialPageCount =
    registration.page_count ?? registration.expected_page_count ?? 1;
  const initialActualCount =
    registration.actual_page_count ?? registration.page_count ?? 1;

  const [pageCount, setPageCount] = useState<number>(initialPageCount);
  const [extraCount, setExtraCount] = useState<number>(1);
  const [actualCount, setActualCount] = useState<number>(initialActualCount);
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);

  const setPageCountMutation = useSetPageCountMutation(registration.id);
  const requestExtraMutation = useRequestExtraStickingLabelsMutation(
    registration.id
  );
  const adjustActualMutation = useAdjustActualPageCountMutation(
    registration.id
  );
  const returnMutation = useReturnToRegistrationMutation(registration.id);

  const handleSetPageCount = () => {
    if (pageCount < 1) return;
    setPageCountMutation.mutate({ page_count: pageCount });
  };

  const handleRequestExtra = () => {
    if (extraCount < 1) return;
    requestExtraMutation.mutate({ count: extraCount });
  };

  const handleAdjustActual = () => {
    if (actualCount < 1) return;
    adjustActualMutation.mutate({ actual_page_count: actualCount });
  };

  const handleReturn = () => {
    returnMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmReturnOpen(false);
        onOpenChange(false);
      }
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>
              {t('stickingActionsTitle', {
                number: registration.container_number
              })}
            </DialogTitle>
            <DialogDescription>
              {t('stickingActionsDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-5'>
            {/* 1. Set page count */}
            <section className='space-y-2'>
              <Label
                htmlFor='sticking-page-count'
                className='text-sm font-medium'
              >
                {t('setPageCount')}
              </Label>
              <div className='flex items-end gap-2'>
                <div className='flex-1 space-y-1'>
                  <Label
                    htmlFor='sticking-page-count'
                    className='text-muted-foreground text-xs'
                  >
                    {t('setPageCountField')}
                  </Label>
                  <Input
                    id='sticking-page-count'
                    type='number'
                    min={1}
                    max={100000}
                    value={pageCount}
                    onChange={(e) =>
                      setPageCount(Math.max(1, Number(e.target.value)))
                    }
                  />
                </div>
                <Button
                  type='button'
                  onClick={handleSetPageCount}
                  disabled={setPageCountMutation.isPending || pageCount < 1}
                >
                  {t('setPageCountAction')}
                </Button>
              </div>
            </section>

            {/* 2. Request extra labels — shown once page count is set */}
            {hasPageCount && (
              <>
                <Separator />
                <section className='space-y-2'>
                  <Label
                    htmlFor='sticking-extra-count'
                    className='text-sm font-medium'
                  >
                    {t('requestExtraLabels')}
                  </Label>
                  <div className='flex items-end gap-2'>
                    <div className='flex-1 space-y-1'>
                      <Label
                        htmlFor='sticking-extra-count'
                        className='text-muted-foreground text-xs'
                      >
                        {t('requestExtraField')}
                      </Label>
                      <Input
                        id='sticking-extra-count'
                        type='number'
                        min={1}
                        max={100000}
                        value={extraCount}
                        onChange={(e) =>
                          setExtraCount(Math.max(1, Number(e.target.value)))
                        }
                      />
                    </div>
                    <Button
                      type='button'
                      onClick={handleRequestExtra}
                      disabled={
                        requestExtraMutation.isPending || extraCount < 1
                      }
                    >
                      {t('requestExtraAction')}
                    </Button>
                  </div>
                </section>
              </>
            )}

            {/* 3. Adjust actual page count — shown once page count is set */}
            {hasPageCount && (
              <>
                <Separator />
                <section className='space-y-2'>
                  <Label
                    htmlFor='sticking-actual-count'
                    className='text-sm font-medium'
                  >
                    {t('adjustActualCount')}
                  </Label>
                  <div className='flex items-end gap-2'>
                    <div className='flex-1 space-y-1'>
                      <Label
                        htmlFor='sticking-actual-count'
                        className='text-muted-foreground text-xs'
                      >
                        {t('adjustActualField')}
                      </Label>
                      <Input
                        id='sticking-actual-count'
                        type='number'
                        min={1}
                        max={100000}
                        value={actualCount}
                        onChange={(e) =>
                          setActualCount(Math.max(1, Number(e.target.value)))
                        }
                      />
                    </div>
                    <Button
                      type='button'
                      onClick={handleAdjustActual}
                      disabled={
                        adjustActualMutation.isPending || actualCount < 1
                      }
                    >
                      {t('adjustActualAction')}
                    </Button>
                  </div>
                </section>
              </>
            )}

            {/* 4. Return to registration */}
            <Separator />
            <section className='space-y-2'>
              <Label className='text-sm font-medium'>
                {t('returnToRegistration')}
              </Label>
              <Button
                type='button'
                variant='outline'
                onClick={() => setConfirmReturnOpen(true)}
                disabled={returnMutation.isPending}
                className='w-full'
              >
                <Undo2 className='me-2 h-4 w-4' />
                {t('returnToRegistrationAction')}
              </Button>
            </section>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmReturnOpen} onOpenChange={setConfirmReturnOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('returnToRegistrationConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('returnToRegistrationConfirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={returnMutation.isPending}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleReturn();
              }}
              disabled={returnMutation.isPending}
            >
              {t('returnToRegistrationAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
